import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import ClickAwayListener from '@mui/material/ClickAwayListener';

/** Matches MUI Button size="large" to avoid header height jump on expand. */
const PROFILE_BAR_HEIGHT = 42;

const PROFILE_ROUTES = ['/edit-profile', '/settings'];

const initialsFor = (user) => {
  const letters = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();
  return letters || '?';
};

const ProfileToolbar = ({ user, onLogout }) => {
  const location = useLocation();
  const routePinned = PROFILE_ROUTES.includes(location.pathname);
  const [open, setOpen] = useState(routePinned);

  useEffect(() => {
    setOpen(PROFILE_ROUTES.includes(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const isActive = (path) => location.pathname === path;

  const displayName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User';

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (!routePinned) {
          setOpen(false);
        }
      }}
    >
      <Box
        sx={{
          height: PROFILE_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          ml: 2,
          pl: 0.5,
          pr: 0.5,
          borderRadius: 2,
          backgroundColor: open
            ? 'rgba(0, 0, 0, 0.22)'
            : 'rgba(0, 0, 0, 0.12)',
          boxShadow: open
            ? '0 4px 14px rgba(0, 0, 0, 0.35), inset 0 0 0 1px rgba(255,255,255,0.18)'
            : '0 1px 4px rgba(0, 0, 0, 0.2), inset 0 0 0 1px rgba(255,255,255,0.1)',
          transition: 'background-color 200ms ease, box-shadow 200ms ease',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ height: PROFILE_BAR_HEIGHT }}
        >
          <IconButton
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-label="Profile menu"
            sx={{
              p: 0,
              width: PROFILE_BAR_HEIGHT,
              height: PROFILE_BAR_HEIGHT,
              flexShrink: 0,
            }}
          >
            <Avatar
              src={user?.portrait || undefined}
              alt={displayName}
              sx={{
                width: 36,
                height: 36,
                fontSize: '0.95rem',
                fontWeight: 600,
                bgcolor: 'rgba(255,255,255,0.28)',
                color: '#fff',
                boxShadow: open ? '0 0 0 2px rgba(255,255,255,0.85)' : 'none',
              }}
            >
              {initialsFor(user)}
            </Avatar>
          </IconButton>

          <Collapse
            in={open}
            orientation="horizontal"
            timeout={280}
            unmountOnExit
            sx={{
              height: PROFILE_BAR_HEIGHT,
              alignSelf: 'center',
              '& .MuiCollapse-wrapper': {
                height: PROFILE_BAR_HEIGHT,
              },
              '& .MuiCollapse-wrapperInner': {
                height: PROFILE_BAR_HEIGHT,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ height: PROFILE_BAR_HEIGHT, pl: 0.5 }}
            >
              <Button
                variant="contained"
                color={isActive('/edit-profile') ? 'warning' : 'primary'}
                size="large"
                component={Link}
                to="/edit-profile"
                sx={{ height: PROFILE_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                PROFILE
              </Button>
              <Button
                variant="contained"
                color={isActive('/settings') ? 'warning' : 'primary'}
                size="large"
                component={Link}
                to="/settings"
                sx={{ height: PROFILE_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                SETTINGS
              </Button>
              <Button
                variant="contained"
                color="error"
                size="large"
                onClick={onLogout}
                sx={{ height: PROFILE_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                LOG OUT
              </Button>
            </Stack>
          </Collapse>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
};

export default ProfileToolbar;
